import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Clock, User, ChevronRight, ArrowRight, Tag, ArrowLeft } from 'lucide-react';
import Header from '@/components/public/Header';
import Footer from '@/components/public/Footer';
import { BlogListingSEO } from '@/components/seo/BlogListingSEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BASE_URL = 'https://starteaseai.com';
const POSTS_PER_PAGE = 9;

// Category metadata
const CATEGORY_META = {
  formation: {
    label: 'Business Formation',
    description: 'Comprehensive guides on forming LLCs, corporations, and other business entities in the United States.',
    color: 'bg-emerald-100 text-emerald-700',
  },
  compliance: {
    label: 'Compliance',
    description: 'Stay compliant with federal and state regulations. Learn about annual reports, BOI filings, and more.',
    color: 'bg-blue-100 text-blue-700',
  },
  tax: {
    label: 'Tax & Finance',
    description: 'Tax strategies, EIN registration, and financial guidance for US businesses owned by international entrepreneurs.',
    color: 'bg-amber-100 text-amber-700',
  },
  guides: {
    label: 'Guides',
    description: 'Step-by-step tutorials and how-to guides for every aspect of running a US business.',
    color: 'bg-purple-100 text-purple-700',
  },
  news: {
    label: 'News & Updates',
    description: 'Latest updates on business regulations, StartEase services, and industry news.',
    color: 'bg-rose-100 text-rose-700',
  },
};

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Get category metadata
const getCategoryMeta = (category) => {
  const key = category?.toLowerCase();
  return CATEGORY_META[key] || {
    label: category || 'Category',
    description: `Browse all articles in the ${category} category.`,
    color: 'bg-slate-100 text-slate-700',
  };
};

// Blog Card Component
const BlogCard = ({ post }) => {
  const categoryMeta = getCategoryMeta(post.category);
  
  return (
    <Link 
      to={`/blog/${post.slug}`}
      className="group block bg-white border border-slate-200 overflow-hidden hover:shadow-xl hover:border-emerald-500 hover:-translate-y-1 transition-all duration-300"
      data-testid={`blog-card-${post.slug}`}
    >
      {/* Image */}
      <div className="aspect-video overflow-hidden bg-slate-100">
        {post.featured_image ? (
          <img 
            src={post.featured_image} 
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
            <span className="text-4xl text-slate-300">S</span>
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-6">
        <div className="flex items-center gap-2 mb-3">
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${categoryMeta.color}`}>
            {categoryMeta.label}
          </span>
          {post.reading_time && (
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {post.reading_time} min
            </span>
          )}
        </div>
        
        <h3 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-emerald-600 transition-colors line-clamp-2">
          {post.title}
        </h3>
        
        <p className="text-slate-600 text-sm mb-4 line-clamp-2">
          {post.excerpt || post.meta_description || 'Read more...'}
        </p>
        
        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
          <span className="text-xs text-slate-500">
            {formatDate(post.published_at)}
          </span>
          <span className="text-emerald-600 text-sm font-medium group-hover:translate-x-1 transition-transform flex items-center gap-1">
            Read more <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </Link>
  );
};

const BlogCategoryPage = () => {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPosts, setTotalPosts] = useState(0);
  
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const totalPages = Math.ceil(totalPosts / POSTS_PER_PAGE);
  const categoryMeta = getCategoryMeta(category);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const params = {
          skip: (currentPage - 1) * POSTS_PER_PAGE,
          limit: POSTS_PER_PAGE,
        };
        
        const response = await axios.get(`${API}/public/blog/category/${category}`, { params });
        setPosts(response.data.posts || []);
        setTotalPosts(response.data.total || 0);
        setError(null);
      } catch (err) {
        console.error('Error fetching blog posts:', err);
        setError('Failed to load blog posts');
      } finally {
        setLoading(false);
      }
    };

    if (category) {
      fetchPosts();
    }
  }, [category, currentPage]);

  const handlePageChange = (page) => {
    const params = new URLSearchParams(searchParams);
    if (page > 1) {
      params.set('page', page.toString());
    } else {
      params.delete('page');
    }
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // SEO data
  const pageTitle = `${categoryMeta.label} | Blog | StartEase Agent`;
  const canonical = `${BASE_URL}/blog/category/${category}`;

  return (
    <div className="min-h-screen bg-white" data-testid="blog-category-page">
      <BlogListingSEO
        title={pageTitle}
        description={categoryMeta.description}
        canonical={canonical}
        category={categoryMeta.label}
        posts={posts}
        currentPage={currentPage}
        totalPages={totalPages}
      />

      <Header />

      {/* Hero Section */}
      <section className="bg-[#1e293b] py-16 md:py-20" data-testid="category-hero">
        <div className="container-custom">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-slate-400 mb-6" data-testid="breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">{categoryMeta.label}</span>
          </nav>

          <div className="flex items-center gap-3 mb-4">
            <span className={`text-sm font-medium px-3 py-1 rounded-full ${categoryMeta.color}`}>
              {totalPosts} {totalPosts === 1 ? 'Article' : 'Articles'}
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {categoryMeta.label}
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl">
            {categoryMeta.description}
          </p>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-12 md:py-16" data-testid="category-posts-section">
        <div className="container-custom">
          {/* Back to Blog Link */}
          <Link 
            to="/blog"
            className="inline-flex items-center gap-2 text-slate-600 hover:text-emerald-600 mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to all posts
          </Link>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-pulse text-lg text-slate-500">Loading posts...</div>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-red-500 mb-4">{error}</p>
              <button 
                onClick={() => window.location.reload()}
                className="text-emerald-600 font-medium hover:underline"
              >
                Try again
              </button>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <Tag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No posts found</h3>
              <p className="text-slate-600 mb-6">
                No posts in this category yet. Check back soon!
              </p>
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-emerald-600 font-medium hover:underline"
              >
                <ArrowLeft className="w-4 h-4" />
                View all posts
              </Link>
            </div>
          ) : (
            <>
              {/* Posts Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {posts.map((post) => (
                  <BlogCard key={post.id || post.slug} post={post} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2" data-testid="pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-4 py-2 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`w-10 h-10 rounded font-medium transition-colors ${
                        page === currentPage
                          ? 'bg-emerald-600 text-white'
                          : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Related Categories */}
      <section className="bg-slate-50 py-12" data-testid="related-categories">
        <div className="container-custom">
          <h2 className="text-xl font-semibold text-slate-900 mb-6">Explore Other Topics</h2>
          <div className="flex flex-wrap gap-3">
            {Object.entries(CATEGORY_META)
              .filter(([key]) => key !== category?.toLowerCase())
              .map(([key, meta]) => (
                <Link
                  key={key}
                  to={`/blog/category/${key}`}
                  className="px-4 py-2 bg-white border border-slate-200 rounded-full text-sm font-medium text-slate-600 hover:border-emerald-500 hover:text-emerald-600 transition-colors"
                >
                  {meta.label}
                </Link>
              ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogCategoryPage;
